import React from 'react'
import styled, { keyframes } from 'styled-components'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors';
import CardTickets from './card/CardTickets';
import breakpoints from 'theme/breakpoints';
import { useEventDetailTab } from 'context/dash/EventDetailTab';
import { Ticket, Store } from 'lucide-react';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

function fmtCurrency(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function TicketsForSale({ data, offlineStats }) {
    const { setEventDetailTab } = useEventDetailTab();

    const tickets = data?.filter((el) => el.type === 'ticket') || [];

    return (
        <Container>
            <SectionHeader>
                <HeaderIconWrap>
                    <Ticket size={16} color={panelAccent(colors.secondaryPurple)} />
                </HeaderIconWrap>
                <SectionLabel>Ingressos à venda</SectionLabel>
                <TicketCount>{tickets.length}</TicketCount>
            </SectionHeader>

            <TicketsGrid>
                {tickets.map((el) => (
                    <CardTickets
                        key={el.id}
                        data={el}
                        onTicketClick={() => setEventDetailTab && setEventDetailTab('tickets')}
                    />
                ))}
            </TicketsGrid>

            {offlineStats && (
                <OfflineSection>
                    <OfflineLabel>
                        <Store size={12} />
                        Vendas no PDV
                    </OfflineLabel>
                    <OfflineCards>
                        <OfflineCard>
                            <OfflineCardValue>{offlineStats.sold}</OfflineCardValue>
                            <OfflineCardLabel>Ingressos vendidos</OfflineCardLabel>
                        </OfflineCard>
                        <OfflineDivider />
                        <OfflineCard>
                            <OfflineCardValue>{fmtCurrency(offlineStats.revenue)}</OfflineCardValue>
                            <OfflineCardLabel>Receita total</OfflineCardLabel>
                        </OfflineCard>
                    </OfflineCards>
                    <OfflineHint>Vendas realizadas pelo caixa presencial vinculado a este evento</OfflineHint>
                </OfflineSection>
            )}
        </Container>
    );
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled(Column)`
    width: 100%;
    gap: 0;
    animation: ${fadeUp} 0.3s ease both;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const HeaderIconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '14')};
    flex-shrink: 0;
`;

const SectionLabel = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    flex: 1;
`;

const TicketCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 20px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '18')};
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: 11px;
    font-weight: 600;
`;

const TicketsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;

    @media screen and (min-width: ${breakpoints.smd}) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
    }
`;

/* ── Offline PDV Stats ── */
const OfflineSection = styled.div`
    margin-top: 20px;
    padding: 14px 16px;
    border-radius: 12px;
    background: ${colors.neutralBackground};
    border: 1px solid rgba(0,0,0,0.06);
`;

const OfflineLabel = styled.p`
    display: flex;
    align-items: center;
    gap: 5px;
    margin: 0 0 12px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: ${colors.nightLight};
`;

const OfflineCards = styled.div`
    display: flex;
    align-items: center;
    gap: 0;
`;

const OfflineCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const OfflineDivider = styled.div`
    width: 1px;
    height: 36px;
    background: rgba(0,0,0,0.1);
    margin: 0 16px;
`;

const OfflineCardValue = styled.p`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    letter-spacing: -0.5px;
`;

const OfflineCardLabel = styled.p`
    margin: 0;
    font-size: 11px;
    color: ${colors.nightLight};
`;

const OfflineHint = styled.p`
    margin: 10px 0 0;
    font-size: 11px;
    color: ${colors.nightLight};
    font-style: italic;
    line-height: 1.4;
`;
